import { CheckButton } from '@apply/components';
import {
    useBorrowerEsignTermSheetFont,
    useBorrowerEsignTypedName,
    useBorrowerEsignTitle,
    useBorrowerEsignDate,
    useBorrowerEsignIpAddress,
} from '../../../../../queries/BorrowerFields/EsignFields';
import { useBorrowerLoanType } from '../../../../../queries/BorrowerFields/CoreFields';
import { useBorrowerPrincipalEmail, useBorrowerPrincipalPhone } from '../../../../../queries/BorrowerFields/PrincipalFields';
import { useBorrowerEntityName } from '../../../../../queries/BorrowerFields/EntityFields';
import { ApplyApi } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../../../../ApplicationInsights';
import { constructAgreementHtml } from '../../functions/constructAgreementHtml';
import { BuildRentType, LoanType, PreferredTermType } from '@apply/constants';
import { usePropertiesCache } from '../../../../Property/queries/usePropertiesCache';

interface AgreeTermsheetParameters {
    width?: string;
    set_termSheetHtml: React.Dispatch<React.SetStateAction<string | null>>;
}

export const AgreeTermsheet: React.FC<AgreeTermsheetParameters> = ({ set_termSheetHtml, width = 'auto' }) => {
    const applyApi = new ApplyApi(applicationInsights);

    const [esignTermSheetFont, set_esignTermSheetFont] = useBorrowerEsignTermSheetFont();
    const [loanType] = useBorrowerLoanType();
    const [typedName] = useBorrowerEsignTypedName();
    const [title] = useBorrowerEsignTitle();
    const [date] = useBorrowerEsignDate();
    const [ipAddress] = useBorrowerEsignIpAddress();
    const [principalEmail] = useBorrowerPrincipalEmail();
    const [entityName] = useBorrowerEntityName();
    const [principalPhone] = useBorrowerPrincipalPhone();

    const properties = usePropertiesCache();

    const onClickTermSheetAgreement = async () => {
        if (!loanType) return;

        if (esignTermSheetFont) {
            set_esignTermSheetFont(null);
            return;
        }

        const buildRentType = getBuildRentType();
        const preferredTermType = getPreferredTermType();
        const response = await applyApi.getTermSheetHtml({
            logoUrl: `${window.location.origin}/assets/img/header-logo.png`,
            rentalExhibitImageUrl: `${window.location.origin}/assets/img/rental-exhibit.png`,
            loanType: loanType,
            borrowerName: typedName ?? '',
            borrowerEmail: principalEmail ?? '',
            borrowerEntity: entityName ?? '',
            borrowerPhone: principalPhone ?? '',
            borrowerTitle: title ?? '',
            buildRentType,
            preferredTermType,
        });

        if (response.statusCode !== 200) return;

        set_termSheetHtml(
            constructAgreementHtml(response.result, typedName ?? '', title ?? '', date ?? '', undefined, ipAddress ?? '')
        );
    };

    const getBuildRentType = (): BuildRentType | undefined => {
        if (loanType != LoanType.BuildRent) return undefined;

        if (properties.some((p) => p.rental?.buildRentTypeId === BuildRentType.BuildRent)) return BuildRentType.BuildRent;
        if (properties.some((p) => p.rental?.buildRentTypeId === BuildRentType.FixRent)) return BuildRentType.FixRent;

        return undefined;
    };

    const getPreferredTermType = (): PreferredTermType | undefined => {
        if (loanType != LoanType.BuildRent) return undefined;

        if (properties.some((p) => p.rental?.preferredTermType === PreferredTermType.Thirty)) return PreferredTermType.Thirty;
        if (properties.some((p) => p.nonRental?.preferredTermType === PreferredTermType.Thirty)) return PreferredTermType.Thirty;

        return undefined;
    };

    return (
        <CheckButton
            id='agree-termsheet'
            label='Term Sheet'
            selected={!!esignTermSheetFont}
            onToggle={onClickTermSheetAgreement}
            width={width}
        />
    );
};
